import React, { useEffect, useState } from 'react';
import {
  Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography, Chip, makeStyles, CircularProgress, Box,
} from '@material-ui/core';
import { analyticsService } from '../../services';
import { PRODUCTS } from '../../constants/product.constant';
import { fmtNum, fmtDateTimeIST } from '../../components/analytics/format';

const useStyles = makeStyles(() => ({
  wrap: { padding: 18 },
  title: { fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 4 },
  sub: { fontSize: 12, color: '#9ca3af', marginBottom: 12 },
  chip: { marginRight: 4, marginBottom: 2, height: 22 },
  muted: { color: '#9ca3af' },
}));

type Signup = {
  _id: string;
  name?: string | null;
  mobile?: string | null;
  email?: string | null;
  userType?: string | null;
  products: string[];
  platforms: string[];
  createdOn: string;
};

const productLabel = (key: string) => PRODUCTS.find((p) => p.key === key)?.label || key;

const RecentSignupsTab: React.FC = () => {
  const classes = useStyles();
  const [signups, setSignups] = useState<Signup[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const res = await analyticsService.getRecentSignups();
      if (!mounted) return;
      if (res.ok) setSignups(res.data?.signups || []);
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <Paper className={classes.wrap} elevation={1}>
      <Typography className={classes.title}>Recent signups ({fmtNum(signups.length)})</Typography>
      <Typography className={classes.sub}>Last 2 days · times in IST</Typography>
      {loading ? (
        <Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>
      ) : (
        <div style={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time (IST)</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Mobile / Email</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Platform</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {signups.map((s) => (
              <TableRow key={s._id}>
                <TableCell>{fmtDateTimeIST(s.createdOn)}</TableCell>
                <TableCell>{s.name || <span className={classes.muted}>—</span>}</TableCell>
                <TableCell>{s.mobile || s.email || <span className={classes.muted}>—</span>}</TableCell>
                <TableCell>
                  {s.products.length
                    ? s.products.map((p) => <Chip key={p} className={classes.chip} size="small" label={productLabel(p)} />)
                    : <span className={classes.muted}>—</span>}
                </TableCell>
                <TableCell>
                  {s.platforms.length
                    ? s.platforms.map((p) => <Chip key={p} className={classes.chip} size="small" variant="outlined" label={p} />)
                    : <span className={classes.muted}>—</span>}
                </TableCell>
              </TableRow>
            ))}
            {signups.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="textSecondary">No signups in the last 2 days.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </div>
      )}
    </Paper>
  );
};

export default RecentSignupsTab;
